<template>
    <filters-wrapper
        :has-active-filters="props.hasActiveFilters"
        :is-expanded="true"
        @submit="emit('submit')"
        @reset="resetFilter"
    >
        <template v-slot:panel>
            <v-switch
                v-if="!isHideInactiveFilter"
                v-model="filters.active"
                :label="filters.active ? $t('active') : $t('inactive')"
                color="primary"
                class="px-6"
                hide-details
                data-cy="show-active-companies"
                @update:model-value="emit('submit')"
            />
        </template>
        <v-form class="table-filters">
            <v-row dense>
                <v-col cols="12" md="3">
                    <v-text-field
                        v-model="filters.name"
                        :label="$t('name')"
                        hide-details
                        density="compact"
                        variant="outlined"
                        color="primary"
                        data-cy="name"
                        class="mb-4"
                    />
                    <v-text-field
                        v-model="filters.hotelCode"
                        :label="$t('hotel_code')"
                        hide-details
                        density="compact"
                        variant="outlined"
                        color="primary"
                        data-cy="name"
                        class="mb-4"
                    />
                </v-col>
                <v-col cols="12" md="3" sm="6">
                    <v-autocomplete
                        ref="countryRef"
                        v-model="filters.countryId"
                        :items="useCountries"
                        item-title="title"
                        item-value="value"
                        :label="$t('country')"
                        closable-chips
                        hide-details
                        clearable
                        density="compact"
                        color="primary"
                        data-cy="country-id"
                        class="mb-4"
                        @update:model-value="updateCities()"
                    />
                    <v-autocomplete
                        ref="cityRef"
                        v-model="filters.cityId"
                        :items="cities"
                        item-title="title"
                        item-value="value"
                        :label="$t('city')"
                        :loading="isCitiesLoading"
                        :disabled="isCitiesLoading || !cities.length"
                        closable-chips
                        hide-details
                        clearable
                        density="compact"
                        color="primary"
                        data-cy="city-id"
                        class="mb-4"
                    />
                </v-col>
                <v-col cols="12" md="3" sm="6">
                    <v-autocomplete
                        v-model="filters.category"
                        :items="categoryDictionary"
                        item-title="name"
                        item-value="code"
                        :label="$t('category')"
                        density="compact"
                        :loading="isLoadingSuppliers"
                        clearable
                        color="primary"
                        data-cy="category"
                        hide-details
                        class="mb-4"
                    />
                    <v-row dense>
                        <v-col cols="6">
                            <v-text-field
                                v-model="filters.ratingFrom"
                                :label="$t('rating_from')"
                                hide-details
                                density="compact"
                                variant="outlined"
                                color="primary"
                                data-cy="rating-from"
                                class="mb-4"
                            />
                        </v-col>
                        <v-col cols="6">
                            <v-text-field
                                v-model="filters.ratingTo"
                                :label="$t('rating_to')"
                                hide-details
                                density="compact"
                                variant="outlined"
                                color="primary"
                                data-cy="rating-to"
                                class="mb-4"
                            />
                        </v-col>
                    </v-row>
                </v-col>
                <v-col cols="12" md="3" sm="6">
                    <v-autocomplete
                        v-model="filters.contentProviderId"
                        :items="useContentProviderIdItems"
                        :label="$t('source_of_description')"
                        item-title="name"
                        item-value="id"
                        density="compact"
                        :loading="isLoadingSuppliers"
                        clearable
                        color="primary"
                        data-cy="source-of-description"
                        hide-details
                        class="mb-4"
                        :disabled="!isShowFullListOfContentProviders"
                    />
                    <v-text-field
                        v-model="filters.groupId"
                        :label="$t('group_number')"
                        hide-details
                        density="compact"
                        variant="outlined"
                        color="primary"
                        data-cy="group-number"
                        class="mb-4"
                    />
                </v-col>
            </v-row>
        </v-form>
    </filters-wrapper>
</template>

<script setup lang="ts">
    import type {SelectOptionType} from '~/utils/types/types'
    import {useCountries} from '~/composables/form-helpers'
    import type {AccommodationDescriptionFilterFilterType} from '~/utils/types/account/products/types'

    const props = defineProps<{
        hasActiveFilters: boolean
        isHideInactiveFilter?: boolean
        isShowFullListOfContentProviders?: boolean
    }>()

    const filters = defineModel<AccommodationDescriptionFilterFilterType>()
    const emit = defineEmits<{
        (e: 'submit' | 'reset'): void
    }>()

    const isCitiesLoading = ref<boolean>(false)
    const cities = ref<SelectOptionType[]>([])
    const cityRef = ref()
    const countryRef = ref()
    const isLoadingSuppliers = ref(false)
    const categoryDictionary = ref([])

    const app = useNuxtApp()
    const {t} = useI18n()

    watch(useCountries, async () => {
        if (filters.value.countryId) await loadCities()
    })

    onMounted(async () => {
        await loadHotelCategoryDictionaryInfo()
    })

    async function loadHotelCategoryDictionaryInfo(): Promise<void> {
        try {
            const dictionary = await app.$api.hotelCategoryDictionaryInfo.get({
                supplierId: 1,
            })
            categoryDictionary.value = dictionary.sort((a, b) => a.name.localeCompare(b.name))
        } catch (e) {
            categoryDictionary.value = []
        }
    }

    const updateCities = async (): Promise<void> => {
        cities.value = []
        if (filters.value.cityId) cityRef.value.reset()
        if (filters.value.countryId) await loadCities()
    }

    const useContentProviderIdItems = computed(() => {
        return [{name: t('own_description'), id: 1}, ...supplierStore.contentProviders]
    })

    async function loadCities() {
        try {
            isCitiesLoading.value = true
            const rs = await app.$api.locations.get({
                limitCities: -1,
                orderBy: 'POPULARITY',
                countryId: filters.value.countryId,
            })
            if (rs.cities) {
                cities.value = rs.cities.map(({id, name}) => {
                    return {title: name, value: id}
                })
            }
        } catch (e) {
            cities.value = []
        } finally {
            isCitiesLoading.value = false
        }
    }

    const resetFilter = (): void => {
        cities.value = []
        if (filters.value.countryId) countryRef.value.reset()
        if (filters.value.cityId) cityRef.value.reset()
        emit('reset')
    }
</script>
